/**
 * « Photographie les pages de ton cahier de cette semaine » — lot P5.
 *
 * En fin de semaine (du vendredi 16 h au dimanche 18 h, heure de Paris),
 * l'enfant photographie les pages de son cahier de classe, matière par matière,
 * autant de pages qu'il veut. Un modèle les lit ensuite ; ce qu'il en tire —
 * compétences reconnues, blocages — est pour le parent, **jamais montré ici**.
 *
 * **C'est le serveur qui décide d'afficher la carte** (`a_demander`), pour la
 * même raison que l'heure d'ouverture du journal du soir : l'horloge d'une
 * tablette se règle à la main. L'écran ne refait aucun calcul d'heure.
 *
 * **Elle ne ferme rien.** Ni redirection, ni blocage du travail du soir. Tant
 * qu'elle charge elle n'occupe rien ; si la lecture échoue elle ne s'affiche
 * pas, et le reste de l'écran continue.
 *
 * Une fois montrée, elle **reste** tant que la fenêtre est ouverte, même quand
 * la dernière matière vient d'être photographiée et que `a_demander` passe à
 * faux : sinon la carte disparaîtrait sous le doigt de l'enfant, qui ne
 * pourrait plus retirer la page floue qu'il vient d'envoyer.
 *
 * Aucun décompte du type « 3 sur 5 » : une matière faite est marquée faite, et
 * c'est tout (§14.1).
 *
 * La photo part au stockage par URL présignée (`uploadPhoto`, invariant 7) ; le
 * même utilitaire que la photo de l'énoncé, qui redessine l'image en JPEG avant
 * l'envoi. Seuls JPEG, PNG et WebP sont acceptés en entrée : le HEIC n'est pas
 * décodé par tous les navigateurs, et une image que le navigateur ne sait pas
 * redessiner ne partirait pas.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import type { MatiereDuCahierOut, MonCahierDeLaSemaineOut } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { Button, Card, ErrorNote } from "@/ui/primitives";

/** Les formats que la carte accepte, pour l'attribut `accept` et la vérification. */
export const FORMATS_ACCEPTES = ["image/jpeg", "image/png", "image/webp"] as const;

/**
 * Vrai si le fichier est d'un format accepté.
 *
 * Un type vide (certains navigateurs n'en donnent pas pour une photo prise à
 * l'appareil) est laissé passer : `uploadPhoto` le redessinera en JPEG ou
 * échouera, et l'échec est alors dit comme n'importe quel envoi raté.
 */
export function formatAccepte(type: string): boolean {
  if (!type) return true;
  return (FORMATS_ACCEPTES as readonly string[]).includes(type.toLowerCase());
}

type Envoi =
  | { quoi: "repos" }
  | { quoi: "envoi"; matiere: string }
  | { quoi: "panne"; matiere: string; message: string };

export function CahierDeLaSemaine() {
  const [cahier, setCahier] = useState<MonCahierDeLaSemaineOut | null>(null);
  // Montrée une fois, la carte reste tant que la fenêtre est ouverte.
  const [montree, setMontree] = useState(false);
  const [envoi, setEnvoi] = useState<Envoi>({ quoi: "repos" });

  const recevoir = useCallback((reponse: MonCahierDeLaSemaineOut | null | undefined) => {
    if (!reponse) {
      setCahier(null);
      return;
    }
    setCahier(reponse);
    if (reponse.a_demander) setMontree(true);
  }, []);

  useEffect(() => {
    let vivant = true;
    call("monCahierDeLaSemaine")
      .then((reponse) => {
        if (vivant) recevoir(reponse);
      })
      .catch(() => {
        // Panne ou refus : la carte ne s'affiche pas, rien d'autre ne bouge.
        if (vivant) setCahier(null);
      });
    return () => {
      vivant = false;
    };
  }, [recevoir]);

  if (!cahier) return null;
  const visible = cahier.a_demander || (montree && cahier.ouverte);
  if (!visible) return null;

  const photographier = async (matiere: string, file: File) => {
    if (!formatAccepte(file.type)) {
      setEnvoi({
        quoi: "panne",
        matiere,
        message: "Cette image n'est pas dans un format que je sais lire. Prends une photo ou choisis un JPEG, PNG ou WebP.",
      });
      return;
    }
    setEnvoi({ quoi: "envoi", matiere });
    try {
      const photo = await uploadPhoto(file);
      recevoir(await call("photographierMonCahier", { body: { matiere, key: photo.key } }));
      setEnvoi({ quoi: "repos" });
    } catch {
      setEnvoi({ quoi: "panne", matiere, message: "La photo n'est pas partie. Réessaie." });
    }
  };

  const retirer = async (matiere: string, photoId: string) => {
    try {
      recevoir(await call("retirerUnePageDeMonCahier", { params: { photo_id: photoId } }));
    } catch {
      setEnvoi({ quoi: "panne", matiere, message: "La page n'a pas pu être retirée. Réessaie." });
    }
  };

  const matieres = cahier.matieres ?? [];

  return (
    <Card title="Le cahier de la semaine">
      <p className="muted small">Photographie les pages de ton cahier de cette semaine.</p>
      <ul className="devoirs__liste">
        {matieres.map((matiere) => (
          <Matiere
            key={matiere.matiere}
            matiere={matiere}
            envoi={envoi}
            photographier={(file) => void photographier(matiere.matiere, file)}
            retirer={(id) => void retirer(matiere.matiere, id)}
          />
        ))}
      </ul>
    </Card>
  );
}

function Matiere({
  matiere,
  envoi,
  photographier,
  retirer,
}: {
  matiere: MatiereDuCahierOut;
  envoi: Envoi;
  photographier: (file: File) => void;
  retirer: (photoId: string) => void;
}) {
  const pages = matiere.pages ?? [];
  const faite = pages.length > 0;
  const enCours = envoi.quoi === "envoi" && envoi.matiere === matiere.matiere;
  const panne = envoi.quoi === "panne" && envoi.matiere === matiere.matiere ? envoi.message : null;

  return (
    <li className="devoirs__ligne" data-matiere={matiere.matiere}>
      <p className="devoirs__entete">
        <strong>{matiere.libelle}</strong>
        {faite ? <span className="muted small"> · fait</span> : null}
      </p>

      {faite ? (
        <ul className="devoirs__photos">
          {pages.map((page, index) => (
            <li key={page.id}>
              {page.url ? (
                <a href={page.url} target="_blank" rel="noreferrer">
                  <img
                    className="devoirs__photo"
                    src={page.url}
                    alt={`Page ${index + 1} du cahier de ${matiere.libelle}`}
                    loading="lazy"
                  />
                </a>
              ) : (
                <span className="muted small">Image indisponible</span>
              )}
              <Button variant="quiet" onClick={() => retirer(page.id)}>
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <label className="devoirs__prise">
        <span className="muted small">{faite ? "Ajouter une autre page." : "Ajouter une page."}</span>
        <input
          className="input"
          type="file"
          accept={FORMATS_ACCEPTES.join(",")}
          capture="environment"
          aria-label={`Ajouter une page de ${matiere.libelle}`}
          disabled={enCours}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) photographier(file);
          }}
        />
      </label>
      {enCours ? <p className="muted small">Envoi de la photo…</p> : null}
      {panne ? <ErrorNote>{panne}</ErrorNote> : null}
    </li>
  );
}
